import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Placeholder hints per template (shown to the editor)
const VARS = {
  welcome: ['name', 'portal_url'],
  ticket_assigned: ['name', 'ticket_id', 'assigned_by', 'assigned_at', 'portal_url'],
  ticket_completed: ['name', 'ticket_id', 'completed_by', 'completed_at', 'resolution', 'portal_url'],
  ticket_feedback: ['name', 'ticket_id', 'portal_url'],
};
const statusBadge = s => {
  const map = { sent:'badge-completed', failed:'badge-faulty', skipped:'badge-pending' };
  return <span className={`badge ${map[s] || 'badge-normal'}`}>{s}</span>;
};

export default function MailCenter() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('templates');
  const [digestBusy, setDigestBusy] = useState(false);
  const sendDigest = async () => {
    if (!window.confirm('Email every IT person their pending tasks now?\n\nEach mail is CC’d to the IT notify address.')) return;
    setDigestBusy(true);
    try {
      const r = await api.post('/mail/pending-digest');
      window.alert(r.data.message || `Sent ${r.data.sent} digest email(s) covering ${r.data.pending} pending task(s).`);
    } catch (e) { window.alert(e.response?.data?.error || 'Could not send the digest'); }
    finally { setDigestBusy(false); }
  };
  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;
  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Email Notifications</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Edit the messages sent to staff and review delivery details.</p>
      </div>
      <div className="flex-gap" style={{ gap:8, marginBottom:16, justifyContent:'space-between', flexWrap:'wrap' }}>
        <div className="flex-gap" style={{ gap:8 }}>
          {[['templates','✉️ Templates'],['log','📬 Delivery Log']].map(([id,label]) => (
            <button key={id} className={`btn btn-sm ${tab===id?'btn-primary':'btn-secondary'}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" disabled={digestBusy} onClick={sendDigest}
          title="Email each IT person their open tasks, CC'd to the IT inbox">
          {digestBusy ? 'Sending…' : '📋 Send pending-task digest now'}
        </button>
      </div>
      {tab === 'templates' ? <Templates /> : <DeliveryLog />}
    </div>
  );
}

function Templates() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [testTo, setTestTo] = useState('');
  const load = useCallback(() => { api.get('/mail/templates').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); }, [load]);

  const open = t => { setEdit({ ...t }); setMsg(''); setTestTo(''); };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await api.put(`/mail/templates/${edit.key}`, { subject:edit.subject, title:edit.title, body:edit.body, enabled:edit.enabled });
      setMsg('✓ Saved'); load(); setTimeout(()=>setMsg(''),2000);
    } catch(e) { setMsg(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const test = async () => {
    if (!testTo.trim()) { setMsg('Enter a test recipient email'); return; }
    setBusy(true); setMsg('Sending…');
    try { const r = await api.post(`/mail/templates/${edit.key}/test`, { to: testTo.trim() });
      setMsg(r.data.ok ? '✓ Preview sent' : (r.data.message || 'Could not send')); }
    catch(e) { setMsg(e.response?.data?.error || 'Send failed'); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div className="table-wrap"><table>
        <thead><tr><th>Template</th><th>Subject</th><th>Status</th><th></th></tr></thead>
        <tbody>{rows.map(t => (
          <tr key={t.key}>
            <td><div style={{ fontWeight:500 }}>{t.name}</div><div style={{ fontSize:11, color:'var(--text2)' }}>{t.description}</div></td>
            <td style={{ fontSize:13 }}>{t.subject}</td>
            <td>{t.enabled ? <span className="badge badge-completed">On</span> : <span className="badge badge-pending">Off</span>}</td>
            <td><button className="btn btn-secondary btn-sm" onClick={() => open(t)}>Edit</button></td>
          </tr>
        ))}</tbody>
      </table></div>

      {edit && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setEdit(null)}>
          <div className="modal" style={{ maxWidth:680, maxHeight:'92vh', overflowY:'auto' }}>
            <div className="modal-header"><span className="modal-title">{edit.name}</span><button className="modal-close" onClick={() => setEdit(null)}>×</button></div>
            <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:12 }}>
              <input type="checkbox" checked={!!edit.enabled} onChange={e => setEdit(s=>({...s,enabled:e.target.checked}))} /> Send this notification
            </label>
            <div className="form-group"><label>Subject</label>
              <input value={edit.subject||''} onChange={e => setEdit(s=>({...s,subject:e.target.value}))} /></div>
            <div className="form-group"><label>Heading (shown at the top of the email)</label>
              <input value={edit.title||''} onChange={e => setEdit(s=>({...s,title:e.target.value}))} /></div>
            <div className="form-group"><label>Message (HTML)</label>
              <textarea rows={10} style={{ fontFamily:'monospace', fontSize:12 }} value={edit.body||''} onChange={e => setEdit(s=>({...s,body:e.target.value}))} /></div>
            <div style={{ fontSize:11, color:'var(--text2)', background:'var(--bg3)', padding:'8px 10px', borderRadius:6, marginBottom:10 }}>
              Available placeholders (auto-filled when sent): {(VARS[edit.key]||[]).map(v => <code key={v} style={{ marginRight:6 }}>{`{{${v}}}`}</code>)}
              <div style={{ marginTop:4 }}>Your message is wrapped in the branded TIPS header/footer automatically.</div>
            </div>
            <div className="form-group"><label>Send a preview to</label>
              <div className="flex-gap" style={{ gap:8 }}>
                <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@tipsglobeducate.com" style={{ flex:1 }} />
                <button className="btn btn-secondary" onClick={test} disabled={busy}>Send preview</button>
              </div></div>
            {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':msg==='Sending…'?'var(--text2)':'var(--red)', marginBottom:8 }}>{msg}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEdit(null)}>Close</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeliveryLog() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ sent:0, failed:0, skipped:0 });
  const [f, setF] = useState({ status:'', search:'', from:'', to:'' });
  const load = useCallback(() => {
    const params = {}; Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/mail/log', { params }).then(r => setRows(r.data)).catch(()=>{});
  }, [f]);
  useEffect(() => { load(); api.get('/mail/log/stats').then(r => setStats(r.data)).catch(()=>{}); }, [load]);
  const fmt = d => new Date(d).toLocaleString();

  return (
    <div>
      <div className="flex-gap" style={{ gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <span className="badge badge-completed">Sent (30d): {stats.sent}</span>
        <span className="badge badge-faulty">Failed: {stats.failed}</span>
        <span className="badge badge-pending">Skipped: {stats.skipped}</span>
      </div>
      <div className="flex-gap" style={{ gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <select value={f.status} onChange={e => setF(s=>({...s,status:e.target.value}))} style={{ padding:'6px 8px' }}>
          <option value="">All statuses</option><option value="sent">Sent</option><option value="failed">Failed</option><option value="skipped">Skipped</option>
        </select>
        <input value={f.search} onChange={e => setF(s=>({...s,search:e.target.value}))} placeholder="Search email / subject" style={{ padding:'6px 8px', minWidth:200 }} />
        <input type="date" value={f.from} onChange={e => setF(s=>({...s,from:e.target.value}))} style={{ padding:'6px 8px' }} />
        <input type="date" value={f.to} onChange={e => setF(s=>({...s,to:e.target.value}))} style={{ padding:'6px 8px' }} />
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No emails logged yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>When</th><th>To</th><th>Subject</th><th>Type</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmt(r.created_at)}</td>
                <td style={{ fontSize:12 }}>{r.to_email || '—'}{r.user_name ? <div style={{ color:'var(--text2)' }}>{r.user_name}</div> : null}</td>
                <td style={{ fontSize:12 }}>{r.subject}</td>
                <td style={{ fontSize:11, color:'var(--text2)' }}>{r.template_key || '—'}</td>
                <td>{statusBadge(r.status)}</td>
                <td style={{ fontSize:11, color:'var(--red)' }}>{r.error || ''}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
